'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { cn } from '@workspace/ui/lib/utils';
import { MediaDialogScope } from '@workspace/ui/components/media-activity';

const DialogModalContext = React.createContext(true);

function Dialog({ modal = true, ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
    return (
        <DialogModalContext.Provider value={modal}>
            <DialogPrimitive.Root data-slot="dialog" modal={modal} {...props} />
        </DialogModalContext.Provider>
    );
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
    return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
    return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
    return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
    return (
        <DialogPrimitive.Overlay
            data-slot="dialog-overlay"
            className={cn(
                'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50 backdrop-blur-sm',
                className
            )}
            {...props}
        />
    );
}

function DialogContent({
    className,
    children,
    asChild = false,
    showCloseButton = true,
    closeOnOverlayClick = true,
    container,
    overlayClassName,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
    showCloseButton?: boolean;
    closeOnOverlayClick?: boolean;
    container?: HTMLElement | null;
    overlayClassName?: string;
}) {
    const modal = React.useContext(DialogModalContext);
    const overlay = (
        <DialogOverlay
            className={cn(container ? 'absolute inset-0' : undefined, overlayClassName)}
            onClick={(event) => {
                // Radix owns dismissal so a click dismissing a nested menu cannot
                // also close this dialog. Stop React synthetic event propagation
                // across the portal boundary without stopping the native DOM click
                // before it reaches ownerDocument (required for Radix touch dismissal).
                const nativeEvent = event.nativeEvent;
                const nativeStop = nativeEvent.stopPropagation;
                nativeEvent.stopPropagation = () => undefined;
                try {
                    event.stopPropagation();
                } finally {
                    nativeEvent.stopPropagation = nativeStop;
                }
            }}
        />
    );

    return (
        <DialogPortal data-slot="dialog-portal" container={container}>
            {overlay}
            <DialogPrimitive.Content
                data-slot="dialog-content"
                asChild={asChild}
                className={cn(
                    'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-4 shadow-lg outline-none duration-200 sm:max-w-lg',
                    container ? 'absolute top-[50%] left-[50%] z-50' : 'fixed top-[50%] left-[50%] z-50',
                    className
                )}
                onPointerDownOutside={closeOnOverlayClick ? undefined : (e) => e.preventDefault()}
                onInteractOutside={closeOnOverlayClick ? undefined : (e) => e.preventDefault()}
                {...props}
            >
                <MediaDialogScope enabled={modal} slottedContent={asChild ? children : undefined}>
                    {!asChild && children}
                    {showCloseButton && (
                        <DialogPrimitive.Close
                            data-slot="dialog-close"
                            className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 cursor-pointer rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-0 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
                        >
                            <XIcon />
                            <span className="sr-only">Close</span>
                        </DialogPrimitive.Close>
                    )}
                </MediaDialogScope>
            </DialogPrimitive.Content>
        </DialogPortal>
    );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
    return <div data-slot="dialog-header" className={cn('flex flex-col gap-2 text-left sm:text-left', className)} {...props} />;
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
    return <div data-slot="dialog-footer" className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)} {...props} />;
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
    return <DialogPrimitive.Title data-slot="dialog-title" className={cn('text-lg leading-none font-semibold', className)} {...props} />;
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
    return <DialogPrimitive.Description data-slot="dialog-description" className={cn('text-muted-foreground text-sm', className)} {...props} />;
}

export { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger };
